import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type PickedUpFilter = "all" | "yes" | "no";

export interface ListFilters {
  /** `null` means "use the default (open) season" — see `ItemListPage`. Kept out of the URL until the operator picks a different one explicitly. */
  seasonId: string | null;
  pickedUp: PickedUpFilter;
  search: string;
  offset: number;
}

export interface ListFiltersApi extends ListFilters {
  setSeason(seasonId: string): void;
  setPickedUp(value: PickedUpFilter): void;
  setSearch(value: string): void;
  setOffset(value: number): void;
}

/**
 * Filter state lives in the URL (`?season=&pickedUp=&q=&offset=`) rather than
 * component state, so a filtered view is linkable and the browser back
 * button steps back through filter changes — required by the brief.
 */
export function useListFilters(): ListFiltersApi {
  const [params, setParams] = useSearchParams();

  const seasonId = params.get("season");
  const pickedUpRaw = params.get("pickedUp");
  const pickedUp: PickedUpFilter = pickedUpRaw === "yes" || pickedUpRaw === "no" ? pickedUpRaw : "all";
  const search = params.get("q") ?? "";
  const offset = Math.max(0, Number(params.get("offset") ?? "0") || 0);

  const setParam = useCallback(
    (key: string, value: string | null, opts: { resetOffset?: boolean } = {}) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
          if (opts.resetOffset !== false) next.delete("offset");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return useMemo(
    () => ({
      seasonId,
      pickedUp,
      search,
      offset,
      setSeason: (value: string) => setParam("season", value),
      setPickedUp: (value: PickedUpFilter) => setParam("pickedUp", value === "all" ? null : value),
      setSearch: (value: string) => setParam("q", value),
      setOffset: (value: number) => setParam("offset", value > 0 ? String(value) : null, { resetOffset: false }),
    }),
    [seasonId, pickedUp, search, offset, setParam],
  );
}
